import React from 'react';
import { clsxMerge, Typography } from '@breezeai-frontend/cargo-ui';

import { formatPrice } from '../../../../../../packages/cargo-ui/src/utils/tools';
import { type BffQuoteModelWithPermissionsQuoteToAdditionalClauseModel } from '../../../generated/api-client';
import { getAppLocale } from '../../../utils/locale';

const getDeductibleAmount = ({
  amount,
  currencyCode,
}: {
  amount: number | undefined;
  currencyCode: string | undefined;
}) => {
  const locale = getAppLocale();
  return amount && currencyCode
    ? formatPrice({
        currency: currencyCode,
        value: amount,
        locale,
      })
    : '';
};

const getDeductibleText = ({
  deductible_minimum_amount,
  deductible_minimum_amount_currency,
  deductible_percentage,
}: DeductibleProperties) => {
  const amount = getDeductibleAmount({
    amount: deductible_minimum_amount,
    currencyCode: deductible_minimum_amount_currency?.code,
  });

  const roundedPercentage = deductible_percentage
    ? parseFloat(deductible_percentage.toFixed(2).toString())
    : '';

  if (amount && roundedPercentage) {
    return `Subject to a deductible of ${roundedPercentage}% (minimum ${amount}) per claim`;
  }

  if (amount) {
    return `Subject to a deductible of ${amount} per claim`;
  }

  if (roundedPercentage) {
    return `Subject to a deductible of ${roundedPercentage}% per claim`;
  }

  return '';
};

export type DeductibleProperties = Pick<
  BffQuoteModelWithPermissionsQuoteToAdditionalClauseModel,
  Extract<
    {
      [K in keyof BffQuoteModelWithPermissionsQuoteToAdditionalClauseModel]: K extends `deductible_${string}`
        ? K
        : never;
    }[keyof BffQuoteModelWithPermissionsQuoteToAdditionalClauseModel],
    keyof BffQuoteModelWithPermissionsQuoteToAdditionalClauseModel
  >
>;

interface DeductibleMessageProps extends DeductibleProperties {
  className?: React.ComponentProps<typeof Typography>['customStyles'];
}

export function DeductibleMessage({
  className,
  ...props
}: DeductibleMessageProps) {
  const text = getDeductibleText(props);

  if (!text) return null;

  return (
    <div className="flex justify-between w-full">
      <Typography
        data-testid="deductible-message"
        customStyles={clsxMerge('text-left', className)}
      >
        {text}
      </Typography>
      {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
      <Typography customStyles={clsxMerge('text-left font-bold', className)}>
        Deductible amount{' '}
        {getDeductibleAmount({
          amount: props.deductible_minimum_amount,
          currencyCode: props.deductible_minimum_amount_currency?.code,
        })}
      </Typography>
    </div>
  );
}
